/**
 * Export from the asset page: the whole asset, the programme between its
 * programme in and out timecodes, or the range marked in the player, in a
 * web or broadcast format, to the exports tier or an export location, with
 * one of its subtitle tracks burnt in or delivered as sidecar files.
 *
 * Original format (0.99.0) exports any asset with no transcode: the whole
 * file byte for byte, or a range of video or audio rewrapped by stream copy
 * (frame-accurate for intra-only originals, widened to keyframes otherwise).
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Download, Loader2, Share, X } from 'lucide-react';
import {
  exportFormat, formatDuration, isIntraOnly, originalExtension, originalFormatLabel, parseTimecode, presetProblem, rewrapContainer, secondsToTicks, ticksToSeconds,
  ticksToTimecodeString, type AssetDetail, type ExportDTO, type ExportFormatId, type MasterChoiceOrAll,
} from '@prism/contracts';
import { MasterPicker } from './MasterPicker';
import { api, errorMessage } from '../api/client';
import { Modal } from './Modal';
import { PresetSelect } from './PresetSelect';
import { DestinationSelect, downloadNote } from './DestinationSelect';
import { toast } from './Toasts';
import { PreflightProblems, RightsCheckBlock, preflightBlocks, preflightRestorable, usePreflight, useRightsCheck } from './Preflight';
import { useExportDestinations, useSubtitles } from '../api/hooks';
import { bytes, relative } from '../lib/format';
import { waitedFor } from './JobProgress';
import { SidecarFormats, sidecarFormatsField } from '../subtitles/SidecarFormats';
import { DEFAULT_SIDECAR_FORMATS, type SidecarFormat } from '@prism/contracts';

type Range = 'full' | 'programme' | 'marked';

export function AssetExportButton({ asset, programme, marked }: {
  asset: AssetDetail;
  programme: { in: string | null; out: string | null };
  /** The player's in and out, in seconds, read when the dialog opens. */
  marked: () => { in: number | null; out: number | null } | undefined;
}) {
  const [open, setOpen] = useState<{ in: number | null; out: number | null } | null>(null);
  // Every asset with media can be exported (0.99.0): video in any format, everything else in its original format.
  if (!asset.versionId) return null;
  return (
    <>
      <button className="btn sm" onClick={() => setOpen(marked() ?? { in: null, out: null })} data-testid="asset-export"><Share size={14} /> Export</button>
      {open && <AssetExportDialog asset={asset} programme={programme} marked={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function AssetExportDialog({ asset, programme, marked, onClose }: {
  asset: AssetDetail; programme: { in: string | null; out: string | null }; marked: { in: number | null; out: number | null }; onClose: () => void;
}) {
  const qc = useQueryClient();
  // Video and audio have a timeline to cut; images, graphics and other files are exported whole.
  const timed = (asset.mediaKind === 'video' || asset.mediaKind === 'audio') && (asset.durationTicks ?? 0) > 0;
  const presets = asset.mediaKind === 'video' && (asset.durationTicks ?? 0) > 0;
  const container = rewrapContainer(asset.originalFilename, asset.technical);
  const hasProgramme = timed && Boolean(programme.in || programme.out);
  const hasMarks = timed && (marked.in != null || marked.out != null);
  const [range, setRange] = useState<Range>(hasMarks ? 'marked' : hasProgramme ? 'programme' : 'full');
  const [preset, setPresetValue] = useState<ExportFormatId>(presets ? 'h264-1080p' : 'original');
  const original = preset === 'original';
  // A part of an original is a rewrap: only for containers FFmpeg writes back as they were.
  const rangeProblem = original && !container
    ? timed ? `A part of a .${originalExtension(asset.originalFilename) || 'file'} original cannot be rewrapped; export the whole file, or choose a format preset` : 'Images, graphics and other files are exported whole'
    : null;
  const what: Range = rangeProblem ? 'full' : range;
  const [qualityChoice, setQuality] = useState<'original' | 'proxy'>(asset.media.originalUrl ? 'original' : 'proxy');
  // The original format is the original: it is never read from the proxy.
  const quality = original ? 'original' : qualityChoice;
  const [destination, setDestination] = useState('');
  const tracks = useSubtitles(asset.id).data?.items.filter((t) => t.cues.length > 0) ?? [];
  const [subs, setSubs] = useState<'none' | 'burn' | 'sidecar' | 'both'>('none');
  const [trackId, setTrackId] = useState('');
  // 1.9.0: which sidecar files (SRT and WebVTT by default).
  const [sidecars, setSidecars] = useState<SidecarFormat[]>([...DEFAULT_SIDECAR_FORMATS]);
  const track = tracks.find((t) => t.id === trackId) ?? tracks[0];
  const setPreset = (p: ExportFormatId) => {
    setPresetValue(p);
    // Burning in needs a transcode: the original format keeps only the sidecar files.
    if (p === 'original' && (subs === 'burn' || subs === 'both')) setSubs(subs === 'both' ? 'sidecar' : 'none');
  };
  // Which master (1.2.0): offered when the asset has more than one; null is the default (Programme closest to the
  // preset; Original format: the primary). A proxy-quality export reads the primary's proxy.
  const masters = asset.masters ?? [];
  const [masterValue, setMaster] = useState<MasterChoiceOrAll | null>(null);
  const pickMasters = quality === 'original' && masters.length > 1;
  const master = pickMasters && masterValue && masterValue.type !== 'all' ? masterValue : null;
  const preflight = usePreflight({
    kind: 'asset-export', assetId: asset.id, quality, locationId: destination || null,
    preset, masterId: master?.type === 'master' ? master.masterId : null,
  });
  const rights = useRightsCheck(preflight.data?.problems);
  const [busy, setBusy] = useState(false);
  // After a start the button rests until something about the export changes, so a second click does not start it twice.
  const [startedFor, setStartedFor] = useState<string | null>(null);
  const destinations = useExportDestinations().data?.items ?? [];
  const exports = useQuery({
    queryKey: ['exports', 'asset', asset.id],
    queryFn: () => api<{ items: ExportDTO[] }>(`/api/v1/exports?assetId=${asset.id}`),
    refetchInterval: (q) => (q.state.data?.items.some((e) => e.status === 'queued' || e.status === 'running') ? 2000 : false),
  });
  const rate = asset.frameRate ?? { num: 25, den: 1 };
  const problem = original ? null : presetProblem(preset, rate);
  const cutNote = what === 'full'
    ? 'The original file, byte for byte'
    : isIntraOnly(asset.technical)
      ? 'Rewrap: the cut is frame-accurate'
      : 'Rewrap: the cut widens to the nearest keyframes, so the start can move up to one GOP earlier and the end up to one GOP later';
  const startFrames = asset.timing.startTimecode ? parseTimecode(asset.timing.startTimecode, rate) ?? 0 : 0;
  const tc = (ticks: number) => ticksToTimecodeString(ticks, rate, startFrames);
  const seconds = (s: number | null, fallback: number) => s ?? fallback;
  const total = (asset.durationTicks ?? 0) / 705_600_000;

  const signature = JSON.stringify([what, preset, quality, destination, subs, track?.id, master, sidecars]);
  const started = startedFor === signature;
  async function start(restore = false) {
    if (busy || started) return;
    setBusy(true);
    try {
      await api(`/api/v1/assets/${asset.id}/exports`, {
        method: 'POST',
        json: {
          preset, quality, range: what, locationId: destination || null, ...(restore ? { restore: true } : {}), ...(master ? { master } : {}),
          subtitleMode: track ? subs : 'none', subtitleTrackId: track && subs !== 'none' ? track.id : null,
          ...(track && (subs === 'sidecar' || subs === 'both') && sidecarFormatsField(sidecars) ? { sidecarFormats: sidecarFormatsField(sidecars) } : {}),
          ...(what === 'marked' ? { inTicks: secondsToTicks(seconds(marked.in, 0)), outTicks: secondsToTicks(seconds(marked.out, total)) } : {}),
          ...rights.field(),
        },
      });
      await qc.invalidateQueries({ queryKey: ['exports', 'asset', asset.id] });
      setStartedFor(signature);
      toast(restore ? 'Restoring the original; the export starts when it is back' : 'Export started');
    } catch (err) {
      if (rights.takeError(err)) toast('Check the rights first', 'error');
      else toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`Export ${asset.title}`} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Close</button>
      {preflightRestorable(preflight) && !started && (
        <button className="btn primary" disabled={busy || Boolean(problem) || !rights.ready} onClick={() => void start(true)} data-testid="restore-and-export">{busy ? <Loader2 size={14} className="spin" /> : <Share size={14} />} Restore and export</button>
      )}
      <button className="btn primary" disabled={busy || started || Boolean(problem) || preflightBlocks(preflight) || !rights.ready} onClick={() => void start()} data-testid="start-asset-export">{busy ? <Loader2 size={14} className="spin" /> : started ? <Check size={14} /> : <Share size={14} />} {started ? 'Export started' : 'Export'}</button>
    </>}>
      <div className="field"><label>What</label>
        <div className="segmented" role="radiogroup" aria-label="What to export" data-testid="asset-export-what">
          <button aria-pressed={what === 'full'} onClick={() => setRange('full')}>{timed ? `Whole asset · ${formatDuration(asset.durationTicks ?? 0)}` : 'Whole asset'}</button>
          {timed && <>
            <button aria-pressed={what === 'programme'} disabled={!hasProgramme || Boolean(rangeProblem)} onClick={() => setRange('programme')} title={rangeProblem ?? (hasProgramme ? undefined : 'Set programme in and out in the metadata, or mark them in the player and choose Set as programme')} data-testid="range-programme">
              Programme {hasProgramme ? `${programme.in ?? 'start'}–${programme.out ?? 'end'}` : '(not set)'}
            </button>
            <button aria-pressed={what === 'marked'} disabled={!hasMarks || Boolean(rangeProblem)} onClick={() => setRange('marked')} title={rangeProblem ?? (hasMarks ? undefined : 'Mark in (I) and out (O) in the player first')} data-testid="range-marked">
              Marked in–out{hasMarks ? ` · ${formatDuration(secondsToTicks(seconds(marked.out, total) - seconds(marked.in, 0)))}` : ''}
            </button>
          </>}
        </div>
        {!timed && <span className="help">Images, graphics and other files are exported whole, in their original format.</span>}
      </div>
      <div className="field-row">
        <div className="field" style={original ? { gridColumn: '1 / -1' } : undefined}><label htmlFor="ax-preset">Format</label>
          <PresetSelect<ExportFormatId> id="ax-preset" value={preset} onChange={setPreset} frameRate={rate}
            original={originalFormatLabel(asset.originalFilename, asset.technical)} originalOnly={!presets} originalHelp={cutNote} />
        </div>
        {!original && (
          <div className="field"><label>Source</label>
            <div className="segmented" role="radiogroup">
              <button aria-pressed={quality === 'original'} onClick={() => setQuality('original')}>Original</button>
              <button aria-pressed={quality === 'proxy'} onClick={() => setQuality('proxy')}>Proxy (review)</button>
            </div>
          </div>
        )}
      </div>
      {pickMasters && (
        <div className="field" data-testid="asset-export-master"><label htmlFor="ax-master">Master</label>
          <MasterPicker id="ax-master" masters={masters} value={masterValue} onChange={setMaster} closestTo={preset} testId="export-master-picker" />
        </div>
      )}
      <div className="field"><label htmlFor="ax-dest">Export to</label>
        <DestinationSelect id="ax-dest" value={destination} onChange={setDestination} destinations={destinations} />
      </div>
      {tracks.length > 0 && (
        <div className="field-row" data-testid="asset-export-subtitles">
          <div className="field"><label htmlFor="ax-subs">Subtitles</label>
            <select id="ax-subs" className="select" value={subs} onChange={(e) => setSubs(e.target.value as typeof subs)} data-testid="asset-export-subs">
              <option value="none">None</option>
              <option value="burn" disabled={original}>Burn in{original ? ' (needs a transcode)' : ''}</option>
              <option value="sidecar">Sidecar files</option>
              <option value="both" disabled={original}>Burn in and sidecar{original ? ' (needs a transcode)' : ''}</option>
            </select>
          </div>
          {subs !== 'none' && (
            <div className="field"><label htmlFor="ax-track">Track</label>
              <select id="ax-track" className="select" value={track?.id ?? ''} onChange={(e) => setTrackId(e.target.value)} data-testid="asset-export-track">
                {tracks.map((t) => <option key={t.id} value={t.id}>{t.label} ({t.language}) · {t.cues.length} cues</option>)}
              </select>
            </div>
          )}
        </div>
      )}
      {tracks.length > 0 && (subs === 'sidecar' || subs === 'both') && <SidecarFormats value={sidecars} onChange={setSidecars} testId="asset-export-sidecars" />}
      <PreflightProblems q={preflight} />
      <RightsCheckBlock check={rights} />
      <p className="section-label">Exports of this asset</p>
      <div className="steps" data-testid="asset-export-list">
        {(exports.data?.items ?? []).length === 0 && <p className="muted" style={{ margin: 0 }}>None yet.</p>}
        {(exports.data?.items ?? []).map((e) => (
          <div className="step" key={e.id} data-status={e.status}>
            <span className={`step-icon ${e.status === 'succeeded' ? 'succeeded' : e.status === 'failed' ? 'failed' : 'running'}`}>
              {e.status === 'succeeded' ? <Check size={12} strokeWidth={3} /> : e.status === 'failed' ? <X size={12} strokeWidth={3} /> : <Loader2 size={12} className="spin" />}
            </span>
            <div className="step-main">
              <span className="step-title">{exportFormat(e).label}{e.quality === 'proxy' ? ' · proxy quality' : ''}{e.master ? <span className="faint" data-testid="export-master"> · from {e.master.label}</span> : null}</span>
              {e.rewrap && <span className="step-sub" data-testid="export-rewrap">{rewrapNote(e.rewrap, tc)}</span>}
              <span className="step-sub">{e.sequenceName.replace(asset.title, '').replace(/^ \(|\)$/g, '')} · {e.waiting ? `Queued · waiting for a free worker${e.waitingSince ? ` · ${waitedFor(e.waitingSince)}` : ''}` : e.status === 'running' || e.status === 'queued' ? `${e.percent != null ? `${Math.round(e.percent)}%` : 'Queued'}` : relative(e.finishedAt ?? e.createdAt)}{e.output?.sizeBytes ? ` · ${bytes(e.output.sizeBytes)}` : ''}{downloadNote(e.download) ? ` · ${downloadNote(e.download)}` : ''}{e.destination ? ` · to ${e.destination.name}` : ''}</span>
              {e.error && <span className="step-sub" style={{ color: 'var(--danger)' }}><AlertTriangle size={11} /> {e.error.message}</span>}
              {e.output?.url && <span style={{ marginTop: 4 }}><a className="btn sm" href={e.output.url} download><Download size={13} /> {exportFormat(e).extension.toUpperCase()}</a></span>}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

/** "Rewrap 10:00:02:00–10:00:05:01 (widened +0.32 s / +0.04 s)", or "… · frame-accurate". */
function rewrapNote(r: NonNullable<ExportDTO['rewrap']>, tc: (ticks: number) => string): string {
  if (r.actualInTicks == null || r.actualOutTicks == null) return `Rewrap ${tc(r.inTicks)}–${tc(r.outTicks)}`;
  const range = `Rewrap ${tc(r.actualInTicks)}–${tc(r.actualOutTicks)}`;
  const early = Math.max(0, r.inTicks - r.actualInTicks);
  const late = Math.max(0, r.actualOutTicks - r.outTicks);
  if (r.exact || (!early && !late)) return `${range} · frame-accurate`;
  const s = (ticks: number) => `+${ticksToSeconds(ticks).toFixed(2)} s`;
  return `${range} (widened ${s(early)} / ${s(late)})`;
}
